// client/src/components/media/MediaEditor.jsx
// In-app image editor with social-media-standard workflow:
//   Adjust (brightness/contrast/saturation/warmth)
//   Filter presets
//   Crop with aspect-ratio presets
//   Rotate / Flip
//   Text overlay with @mention support
// Produces a JPEG blob; caller uploads and preserves the original.

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  FaTimes, FaCheck, FaSun, FaMagic, FaCrop, FaSyncAlt,
  FaFont, FaPlus, FaTrash, FaBold, FaItalic, FaArrowLeft,
} from 'react-icons/fa';
import MentionInput from './MentionInput';
import './MediaEditor.css';

// ── filter preset definitions ────────────────────────────────────────────────
const FILTER_PRESETS = [
  { name: 'None',       values: {} },
  { name: 'Vivid',      values: { saturate: 1.4, contrast: 1.1 } },
  { name: 'Warm',       values: { saturate: 1.1, sepia: 0.25 } },
  { name: 'Cool',       values: { saturate: 0.9, hueRotate: 15, brightness: 1.05 } },
  { name: 'B&W',        values: { saturate: 0, contrast: 1.15 } },
  { name: 'Fade',       values: { contrast: 0.85, brightness: 1.1, saturate: 0.8 } },
  { name: 'Dramatic',   values: { contrast: 1.35, saturate: 1.2, brightness: 0.95 } },
  { name: 'Vintage',    values: { sepia: 0.4, contrast: 0.9, brightness: 1.05 } },
  { name: 'Lomo',       values: { contrast: 1.5, brightness: 1.1, saturate: 1.3 } },
  { name: 'Muted',      values: { saturate: 0.5, brightness: 1.05, contrast: 0.95 } },
];

const CROP_RATIOS = [
  { label: 'Free',  value: null },
  { label: '1:1',   value: 1 },
  { label: '4:5',   value: 4 / 5 },
  { label: '16:9',  value: 16 / 9 },
  { label: '9:16',  value: 9 / 16 },
  { label: '4:3',   value: 4 / 3 },
];

const TABS = ['adjust', 'filter', 'crop', 'rotate', 'text'];

// ── helpers ──────────────────────────────────────────────────────────────────
function buildCSSFilter(adj) {
  const parts = [];
  if (adj.brightness !== undefined && adj.brightness !== 1) parts.push(`brightness(${adj.brightness})`);
  if (adj.contrast   !== undefined && adj.contrast !== 1)   parts.push(`contrast(${adj.contrast})`);
  if (adj.saturate   !== undefined && adj.saturate !== 1)    parts.push(`saturate(${adj.saturate})`);
  if (adj.sepia      !== undefined && adj.sepia !== 0)       parts.push(`sepia(${adj.sepia})`);
  if (adj.hueRotate  !== undefined && adj.hueRotate !== 0)   parts.push(`hue-rotate(${adj.hueRotate}deg)`);
  return parts.length ? parts.join(' ') : 'none';
}

function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

// ─────────────────────────────────────────────────────────────────────────────
export default function MediaEditor({
  src,
  tenantId,
  eventId = null,
  itemId = null,
  onClose,
  onSaved,
}) {
  // ── image loading ──────────────────────────────────────────────────────────
  const imgRef         = useRef(null);
  const previewRef     = useRef(null);      // canvas for live preview
  const containerRef   = useRef(null);
  const [imgLoaded, setImgLoaded]   = useState(false);
  const [dispSize, setDispSize]     = useState({ w: 0, h: 0 });
  const [busy, setBusy]             = useState(false);
  const [error, setError]           = useState('');

  // ── tab state ──────────────────────────────────────────────────────────────
  const [tab, setTab] = useState('adjust');

  // ── adjustments ────────────────────────────────────────────────────────────
  const [adj, setAdj] = useState({
    brightness: 1,
    contrast:   1,
    saturate:   1,
    sepia:      0,
    hueRotate:  0,
  });
  const updateAdj = (k, v) => setAdj(prev => ({ ...prev, [k]: v }));

  // ── active filter preset ───────────────────────────────────────────────────
  const [activePreset, setActivePreset] = useState(0);

  const applyPreset = (idx) => {
    setActivePreset(idx);
    const p = FILTER_PRESETS[idx].values;
    setAdj({
      brightness: p.brightness ?? 1,
      contrast:   p.contrast   ?? 1,
      saturate:   p.saturate   ?? 1,
      sepia:      p.sepia      ?? 0,
      hueRotate:  p.hueRotate  ?? 0,
    });
  };

  // ── rotation / flip ────────────────────────────────────────────────────────
  const [rotation, setRotation] = useState(0);   // degrees, multiples of 90
  const [flipH, setFlipH]       = useState(false);
  const [flipV, setFlipV]       = useState(false);

  // ── crop ───────────────────────────────────────────────────────────────────
  const [cropRatioIdx, setCropRatioIdx] = useState(0);
  // crop rect in 0-1 normalised coords (relative to displayed image)
  const [crop, setCrop] = useState({ x: 0, y: 0, w: 1, h: 1 });
  const [cropping, setCropping] = useState(null); // drag state

  const applyCropRatio = (idx) => {
    setCropRatioIdx(idx);
    const ratio = CROP_RATIOS[idx].value;
    if (!ratio) { setCrop({ x: 0, y: 0, w: 1, h: 1 }); return; }
    const imgRatio = dispSize.w / dispSize.h;
    let w, h;
    if (ratio > imgRatio) { w = 1; h = (dispSize.w / ratio) / dispSize.h; }
    else                  { h = 1; w = (dispSize.h * ratio) / dispSize.w; }
    w = Math.min(w, 1); h = Math.min(h, 1);
    setCrop({ x: (1 - w) / 2, y: (1 - h) / 2, w, h });
  };

  // ── text layers ────────────────────────────────────────────────────────────
  const [layers, setLayers]         = useState([]);
  const [activeLayerId, setActiveLayerId] = useState(null);
  const [draggingLayer, setDraggingLayer] = useState(null);

  const addTextLayer = () => {
    const id = Date.now();
    setLayers(prev => [...prev, {
      id, text: 'Your text', x: 20, y: 20 + prev.length * 50,
      fontSize: 32, color: '#ffffff', bgColor: 'transparent', bold: false, italic: false,
    }]);
    setActiveLayerId(id);
  };
  const updateLayer  = (id, k, v) => setLayers(prev => prev.map(l => l.id === id ? { ...l, [k]: v } : l));
  const removeLayer  = (id) => { setLayers(prev => prev.filter(l => l.id !== id)); if (activeLayerId === id) setActiveLayerId(null); };
  const activeLayer  = layers.find(l => l.id === activeLayerId);

  // ── load source image ──────────────────────────────────────────────────────
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload  = () => { imgRef.current = img; setImgLoaded(true); };
    img.onerror = () => setError('Failed to load image');
    img.src = src;
  }, [src]);

  // ── size preview to container ──────────────────────────────────────────────
  useEffect(() => {
    if (!imgLoaded || !imgRef.current || !containerRef.current) return;
    const img = imgRef.current;
    const el  = containerRef.current;
    const maxW = el.clientWidth - 16;
    const maxH = window.innerHeight * 0.55;
    const ratio = img.naturalWidth / img.naturalHeight;
    let w = maxW, h = w / ratio;
    if (h > maxH) { h = maxH; w = h * ratio; }
    setDispSize({ w: Math.round(w), h: Math.round(h) });
  }, [imgLoaded]);

  // ── draw preview ───────────────────────────────────────────────────────────
  const redraw = useCallback(() => {
    const canvas = previewRef.current;
    const img    = imgRef.current;
    if (!canvas || !img || !dispSize.w) return;

    canvas.width  = dispSize.w;
    canvas.height = dispSize.h;
    const ctx = canvas.getContext('2d');

    ctx.save();
    ctx.filter = buildCSSFilter(adj);

    // rotation + flip
    ctx.translate(dispSize.w / 2, dispSize.h / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
    const swapped = rotation === 90 || rotation === 270;
    const dw = swapped ? dispSize.h : dispSize.w;
    const dh = swapped ? dispSize.w : dispSize.h;
    ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
    ctx.restore();

    // text layers
    layers.forEach(layer => {
      const font = `${layer.italic ? 'italic ' : ''}${layer.bold ? 'bold ' : ''}${layer.fontSize}px sans-serif`;
      ctx.font = font;
      ctx.textBaseline = 'top';
      if (layer.bgColor && layer.bgColor !== 'transparent') {
        const m = ctx.measureText(layer.text || ' ');
        const th = layer.fontSize * 1.3;
        ctx.fillStyle = layer.bgColor;
        ctx.fillRect(layer.x - 4, layer.y - 2, m.width + 8, th + 4);
      }
      ctx.strokeStyle = 'rgba(0,0,0,0.7)';
      ctx.lineWidth   = Math.max(2, layer.fontSize / 12);
      ctx.lineJoin    = 'round';
      ctx.strokeText(layer.text || '', layer.x, layer.y);
      ctx.fillStyle = layer.color;
      ctx.fillText(layer.text || '', layer.x, layer.y);
      if (layer.id === activeLayerId) {
        const m = ctx.measureText(layer.text || ' ');
        const th = layer.fontSize * 1.3;
        ctx.strokeStyle = '#ff6800'; ctx.lineWidth = 2; ctx.setLineDash([5, 3]);
        ctx.strokeRect(layer.x - 6, layer.y - 4, m.width + 12, th + 8);
        ctx.setLineDash([]);
      }
    });

    // crop overlay
    if (tab === 'crop' && !(crop.x === 0 && crop.y === 0 && crop.w === 1 && crop.h === 1)) {
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      const cx = crop.x * dispSize.w, cy = crop.y * dispSize.h;
      const cw = crop.w * dispSize.w, ch = crop.h * dispSize.h;
      // top
      ctx.fillRect(0, 0, dispSize.w, cy);
      // bottom
      ctx.fillRect(0, cy + ch, dispSize.w, dispSize.h - cy - ch);
      // left
      ctx.fillRect(0, cy, cx, ch);
      // right
      ctx.fillRect(cx + cw, cy, dispSize.w - cx - cw, ch);
      // border
      ctx.strokeStyle = '#fff'; ctx.lineWidth = 2; ctx.setLineDash([]);
      ctx.strokeRect(cx, cy, cw, ch);
      // rule-of-thirds
      ctx.strokeStyle = 'rgba(255,255,255,0.3)'; ctx.lineWidth = 1;
      for (let i = 1; i <= 2; i++) {
        ctx.beginPath(); ctx.moveTo(cx + (cw * i) / 3, cy); ctx.lineTo(cx + (cw * i) / 3, cy + ch); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(cx, cy + (ch * i) / 3); ctx.lineTo(cx + cw, cy + (ch * i) / 3); ctx.stroke();
      }
    }
  }, [adj, rotation, flipH, flipV, layers, activeLayerId, crop, tab, dispSize]);

  useEffect(() => { redraw(); }, [redraw]);

  // ── canvas pointer events (crop drag + text drag) ──────────────────────────
  const canvasCoords = (e) => {
    const r = previewRef.current?.getBoundingClientRect();
    if (!r) return { x: 0, y: 0 };
    const cx = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
    const cy = (e.touches ? e.touches[0].clientY : e.clientY) - r.top;
    return { x: cx, y: cy };
  };

  const handlePointerDown = (e) => {
    const { x, y } = canvasCoords(e);

    // text layer hit-test first
    if (tab === 'text') {
      const canvas = previewRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        for (let i = layers.length - 1; i >= 0; i--) {
          const l = layers[i];
          ctx.font = `${l.italic ? 'italic ' : ''}${l.bold ? 'bold ' : ''}${l.fontSize}px sans-serif`;
          const m = ctx.measureText(l.text || ' ');
          const th = l.fontSize * 1.3;
          if (x >= l.x - 6 && x <= l.x + m.width + 6 && y >= l.y - 4 && y <= l.y + th + 4) {
            setActiveLayerId(l.id);
            setDraggingLayer({ id: l.id, ox: x - l.x, oy: y - l.y });
            return;
          }
        }
        setActiveLayerId(null);
      }
    }

    // crop drag
    if (tab === 'crop') {
      const nx = x / dispSize.w, ny = y / dispSize.h;
      setCropping({ startX: nx, startY: ny, origCrop: { ...crop } });
    }
  };

  const handlePointerMove = (e) => {
    const { x, y } = canvasCoords(e);

    if (draggingLayer) {
      e.preventDefault();
      updateLayer(draggingLayer.id, 'x', x - draggingLayer.ox);
      updateLayer(draggingLayer.id, 'y', y - draggingLayer.oy);
      return;
    }

    if (cropping && tab === 'crop') {
      e.preventDefault();
      const nx = clamp(x / dispSize.w, 0, 1);
      const ny = clamp(y / dispSize.h, 0, 1);
      const ratio = CROP_RATIOS[cropRatioIdx].value;

      if (ratio) {
        // fixed ratio: resize from start corner
        let cw = Math.abs(nx - cropping.startX);
        let ch = cw / ratio * (dispSize.w / dispSize.h);
        if (cropping.startY + ch > 1) { ch = 1 - cropping.startY; cw = ch * ratio * (dispSize.h / dispSize.w); }
        cw = clamp(cw, 0.05, 1);
        ch = clamp(ch, 0.05, 1);
        setCrop({
          x: Math.min(cropping.startX, nx),
          y: cropping.startY,
          w: cw,
          h: ch,
        });
      } else {
        // free crop
        const sx = Math.min(cropping.startX, nx);
        const sy = Math.min(cropping.startY, ny);
        setCrop({
          x: sx,
          y: sy,
          w: clamp(Math.abs(nx - cropping.startX), 0.05, 1 - sx),
          h: clamp(Math.abs(ny - cropping.startY), 0.05, 1 - sy),
        });
      }
    }
  };

  const handlePointerUp = () => {
    setDraggingLayer(null);
    setCropping(null);
  };

  // ── final render + save ────────────────────────────────────────────────────
  const handleSave = async () => {
    const img = imgRef.current;
    if (!img) return;
    setBusy(true);
    setError('');

    try {
      // 1. Render full-res with adjustments + rotation
      const natW = img.naturalWidth, natH = img.naturalHeight;
      const swapped = rotation === 90 || rotation === 270;
      const tmpW = swapped ? natH : natW;
      const tmpH = swapped ? natW : natH;

      const tmp = document.createElement('canvas');
      tmp.width = tmpW; tmp.height = tmpH;
      const tc = tmp.getContext('2d');
      tc.filter = buildCSSFilter(adj);
      tc.translate(tmpW / 2, tmpH / 2);
      tc.rotate((rotation * Math.PI) / 180);
      tc.scale(flipH ? -1 : 1, flipV ? -1 : 1);
      tc.drawImage(img, -natW / 2, -natH / 2, natW, natH);

      // 2. Apply crop
      const cx = Math.round(crop.x * tmpW);
      const cy = Math.round(crop.y * tmpH);
      const cw = Math.round(crop.w * tmpW);
      const ch = Math.round(crop.h * tmpH);

      const out = document.createElement('canvas');
      out.width = cw; out.height = ch;
      const oc = out.getContext('2d');
      oc.drawImage(tmp, cx, cy, cw, ch, 0, 0, cw, ch);

      // 3. Text layers (scale coords from display to crop output)
      const scaleX = cw / (crop.w * dispSize.w);
      const scaleY = ch / (crop.h * dispSize.h);

      layers.forEach(layer => {
        const lx = (layer.x - crop.x * dispSize.w) * scaleX;
        const ly = (layer.y - crop.y * dispSize.h) * scaleY;
        const fs = Math.round(layer.fontSize * scaleX);
        const font = `${layer.italic ? 'italic ' : ''}${layer.bold ? 'bold ' : ''}${fs}px sans-serif`;
        oc.font = font;
        oc.textBaseline = 'top';

        if (layer.bgColor && layer.bgColor !== 'transparent') {
          const m = oc.measureText(layer.text || ' ');
          oc.fillStyle = layer.bgColor;
          oc.fillRect(lx - 4 * scaleX, ly - 2 * scaleY, m.width + 8 * scaleX, fs * 1.3 + 4 * scaleY);
        }
        oc.strokeStyle = 'rgba(0,0,0,0.7)';
        oc.lineWidth = Math.max(2, fs / 12);
        oc.lineJoin = 'round';
        oc.strokeText(layer.text || '', lx, ly);
        oc.fillStyle = layer.color;
        oc.fillText(layer.text || '', lx, ly);
      });

      const blob = await new Promise(r => out.toBlob(r, 'image/jpeg', 0.92));

      // 4. Upload
      const fd = new FormData();
      const fname = `edited-${Date.now()}.jpg`;
      fd.append('file', new File([blob], fname, { type: 'image/jpeg' }));
      if (tenantId) fd.append('tenantId', String(tenantId));
      if (eventId)  fd.append('eventId', String(eventId));
      if (itemId)   fd.append('itemId', String(itemId));

      const res = await fetch('/api/media/upload', { method: 'POST', body: fd, credentials: 'include' });
      if (!res.ok) throw new Error('Upload failed');
      const json = await res.json();
      if (!json.url) throw new Error('Upload response missing URL');

      onSaved?.(json.url);
      onClose?.();
    } catch (err) {
      console.error('MediaEditor save failed:', err);
      setError(err.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  // ── reset all edits ────────────────────────────────────────────────────────
  const handleReset = () => {
    setAdj({ brightness: 1, contrast: 1, saturate: 1, sepia: 0, hueRotate: 0 });
    setActivePreset(0);
    setRotation(0);
    setFlipH(false);
    setFlipV(false);
    setCrop({ x: 0, y: 0, w: 1, h: 1 });
    setCropRatioIdx(0);
    setLayers([]);
    setActiveLayerId(null);
  };

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div className="me-overlay">
      {/* ── header ── */}
      <div className="me-header">
        <button className="me-hdr-btn" onClick={onClose} disabled={busy}><FaTimes /></button>
        <span className="me-hdr-title">Edit Image</span>
        <div className="me-hdr-right">
          <button className="me-hdr-btn" onClick={handleReset} disabled={busy} title="Reset all">
            <FaArrowLeft /> Reset
          </button>
          <button className="me-hdr-btn me-hdr-save" onClick={handleSave} disabled={busy}>
            <FaCheck /> {busy ? 'Saving…' : 'Done'}
          </button>
        </div>
      </div>

      {error && <div className="me-error">{error}</div>}

      {/* ── canvas preview ── */}
      <div className="me-stage" ref={containerRef}>
        {!imgLoaded ? (
          <div className="me-loading">Loading…</div>
        ) : (
          <canvas
            ref={previewRef}
            className="me-canvas"
            style={{ width: dispSize.w, height: dispSize.h }}
            onMouseDown={handlePointerDown}
            onMouseMove={handlePointerMove}
            onMouseUp={handlePointerUp}
            onMouseLeave={handlePointerUp}
            onTouchStart={handlePointerDown}
            onTouchMove={handlePointerMove}
            onTouchEnd={handlePointerUp}
          />
        )}
      </div>

      {/* ── tab bar ── */}
      <div className="me-tabs">
        {TABS.map(t => (
          <button
            key={t}
            className={`me-tab${tab === t ? ' active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t === 'adjust' && <FaSun />}
            {t === 'filter' && <FaMagic />}
            {t === 'crop'   && <FaCrop />}
            {t === 'rotate' && <FaSyncAlt />}
            {t === 'text'   && <FaFont />}
            <span>{t.charAt(0).toUpperCase() + t.slice(1)}</span>
          </button>
        ))}
      </div>

      {/* ── tab panels ── */}
      <div className="me-panel">
        {/* ─── ADJUST ─── */}
        {tab === 'adjust' && (
          <div className="me-sliders">
            {[
              { key: 'brightness', label: 'Brightness', min: 0.3, max: 1.7, step: 0.01, dflt: 1 },
              { key: 'contrast',   label: 'Contrast',   min: 0.3, max: 2,   step: 0.01, dflt: 1 },
              { key: 'saturate',   label: 'Saturation', min: 0,   max: 2,   step: 0.01, dflt: 1 },
              { key: 'sepia',      label: 'Warmth',     min: 0,   max: 0.8, step: 0.01, dflt: 0 },
            ].map(s => (
              <div className="me-slider-row" key={s.key}>
                <label>{s.label}</label>
                <input
                  type="range" min={s.min} max={s.max} step={s.step}
                  value={adj[s.key]}
                  onChange={e => { updateAdj(s.key, Number(e.target.value)); setActivePreset(-1); }}
                />
                <span className="me-slider-val">{adj[s.key].toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}

        {/* ─── FILTER ─── */}
        {tab === 'filter' && (
          <div className="me-filter-grid">
            {FILTER_PRESETS.map((p, i) => (
              <button
                key={p.name}
                className={`me-filter-chip${activePreset === i ? ' active' : ''}`}
                onClick={() => applyPreset(i)}
              >
                {p.name}
              </button>
            ))}
          </div>
        )}

        {/* ─── CROP ─── */}
        {tab === 'crop' && (
          <div className="me-crop-ratios">
            {CROP_RATIOS.map((r, i) => (
              <button
                key={r.label}
                className={`me-crop-btn${cropRatioIdx === i ? ' active' : ''}`}
                onClick={() => applyCropRatio(i)}
              >
                {r.label}
              </button>
            ))}
          </div>
        )}

        {/* ─── ROTATE ─── */}
        {tab === 'rotate' && (
          <div className="me-rotate-controls">
            <button className="me-rot-btn" onClick={() => setRotation(r => (r + 90) % 360)}>
              <FaSyncAlt /> Rotate 90°
            </button>
            <button className={`me-rot-btn${flipH ? ' active' : ''}`} onClick={() => setFlipH(f => !f)}>
              Flip H
            </button>
            <button className={`me-rot-btn${flipV ? ' active' : ''}`} onClick={() => setFlipV(f => !f)}>
              Flip V
            </button>
          </div>
        )}

        {/* ─── TEXT ─── */}
        {tab === 'text' && (
          <div className="me-text-panel">
            <button className="me-btn-add-text" onClick={addTextLayer}><FaPlus /> Add Text</button>
            {activeLayer && (
              <div className="me-text-controls">
                <div className="me-text-row">
                  <MentionInput
                    singleLine
                    className="me-text-input"
                    value={activeLayer.text}
                    onChange={val => updateLayer(activeLayer.id, 'text', val)}
                    placeholder="Enter text… type @ to tag"
                    dropdownPosition="above"
                  />
                  <button className="me-btn-del" onClick={() => removeLayer(activeLayer.id)}><FaTrash /></button>
                </div>
                <div className="me-text-row">
                  <label>Size</label>
                  <input type="range" min={12} max={120} value={activeLayer.fontSize}
                    onChange={e => updateLayer(activeLayer.id, 'fontSize', Number(e.target.value))}
                  />
                  <span>{activeLayer.fontSize}px</span>
                </div>
                <div className="me-text-row">
                  <input type="color" value={activeLayer.color}
                    onChange={e => updateLayer(activeLayer.id, 'color', e.target.value)} className="me-color" />
                  <select value={activeLayer.bgColor}
                    onChange={e => updateLayer(activeLayer.id, 'bgColor', e.target.value)} className="me-select">
                    <option value="transparent">No BG</option>
                    <option value="rgba(0,0,0,0.5)">Dark</option>
                    <option value="rgba(255,255,255,0.7)">Light</option>
                    <option value="rgba(0,0,0,0.85)">Solid</option>
                  </select>
                  <button className={`me-toggle${activeLayer.bold ? ' active' : ''}`}
                    onClick={() => updateLayer(activeLayer.id, 'bold', !activeLayer.bold)}><FaBold /></button>
                  <button className={`me-toggle${activeLayer.italic ? ' active' : ''}`}
                    onClick={() => updateLayer(activeLayer.id, 'italic', !activeLayer.italic)}><FaItalic /></button>
                </div>
              </div>
            )}
            {layers.length > 0 && <div className="me-hint">Drag text on image to reposition</div>}
          </div>
        )}
      </div>
    </div>
  );
}
